/**
 * 課本句型跟讀面板 — 口說練習的「句型跟讀」分頁(長期路線圖 P3a)。
 *
 * 每句的核心循環(沿用口試練習既有機制):
 *   ▶ 聽示範  → 離線預產靜態 mp3 優先(tools/gen-shadowing-audio.ts 產),抓不到
 *               才退回即時雲端 TTS(與聽寫同一 /tts 通道)
 *   ⏺ 跟讀錄音 → 收音(MediaRecorder)+ 發音檢查(Web Speech 辨識)一鍵兩用
 *   評分      → speech-check 逐字 multiset 比對,顯示漏唸 / 錯唸的字
 *   追蹤      → 每句最近分數落地 localStorage(shadowing-store,P3c 鋪路)
 *
 * 視覺 = 學術風格設計系統(--ef-* token)。專業介面鐵則:無 UI 音效 / 無彩光 /
 * 無 mascot。學習/遊戲分離:純學習側;錄音只留本機,不上雲。
 */

import { speak, stopSpeaking } from '../../shared/tts-client';
import { academicButton } from '../ui/components';
import { SHADOWING_LESSONS, shadowingAudioUrl, type ShadowingLesson, type ShadowingSentence } from './shadowing-data';
import { recordShadowingScore } from './shadowing-store';
import {
  isRecordingSupported,
  isSpeechCheckSupported,
  ratePronunciation,
  recognizeSpeech,
  scorePronunciation,
  startRecording,
  type RecognitionHandle,
  type RecorderHandle,
} from './speech-check';

/** 面板把手:離開分頁 / 畫面時呼叫 destroy 收尾(停音 / 停錄 / 停辨識)。 */
export interface ShadowingPanelHandle {
  destroy(): void;
}

/**
 * 把句型跟讀面板掛進 `host`(host 先清空)。回傳 destroy 把手。
 */
export function mountShadowing(
  host: HTMLElement,
  lessons: ShadowingLesson[] = SHADOWING_LESSONS,
): ShadowingPanelHandle {
  host.innerHTML = '';

  const speechOk = isSpeechCheckSupported();
  const recordOk = isRecordingSupported();

  let lessonIndex = 0;
  let sentenceIndex = 0;

  // ── 進行中媒體控制把手(換句 / 離開時收尾)──
  let recorder: RecorderHandle | null = null;
  let recognition: RecognitionHandle | null = null;
  let recTimer: ReturnType<typeof setInterval> | null = null;
  let lastUrl: string | null = null;
  let demoAudio: HTMLAudioElement | null = null;

  const stopAll = (): void => {
    stopSpeaking();
    if (demoAudio) { try { demoAudio.pause(); } catch { /* noop */ } demoAudio = null; }
    if (recTimer) { clearInterval(recTimer); recTimer = null; }
    try { recognition?.stop(); } catch { /* noop */ }
    recognition = null;
    if (recorder) { try { void recorder.stop(); } catch { /* noop */ } recorder = null; }
    if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } lastUrl = null; }
  };

  /** 示範:靜態 mp3 優先,error / 不能播才退回即時 TTS(與口試練習同策略)。 */
  const playDemo = (sentence: ShadowingSentence): void => {
    stopSpeaking();
    if (demoAudio) { try { demoAudio.pause(); } catch { /* noop */ } demoAudio = null; }
    let fellBack = false;
    const fallback = (): void => {
      if (fellBack) return;
      fellBack = true;
      void speak(sentence.en, { lang: 'en', voice: 'cloud' });
    };
    let audio: HTMLAudioElement;
    try {
      audio = new Audio(shadowingAudioUrl(sentence));
    } catch { fallback(); return; }
    demoAudio = audio;
    audio.addEventListener('error', fallback, { once: true });
    const p = audio.play() as unknown as Promise<void> | undefined;
    if (p && typeof p.then === 'function') p.catch(fallback);
  };

  // ── 版面骨架 ──
  const intro = document.createElement('p');
  intro.textContent = '選一課,逐句練:先聽示範,再跟著念一次,系統逐字檢查發音。';
  intro.style.cssText = 'font-size:15px;color:var(--ef-ink-soft);line-height:1.7;margin:0 0 14px;';
  host.appendChild(intro);

  if (!speechOk || !recordOk) {
    const note = document.createElement('div');
    note.className = 'shadow-env-note';
    note.style.cssText =
      'font-size:13px;color:var(--ef-sub);background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);padding:10px 12px;margin-bottom:12px;line-height:1.6;';
    const msgs: string[] = [];
    if (!recordOk) msgs.push('此瀏覽器不支援錄音回放。');
    if (!speechOk) msgs.push('此瀏覽器不支援自動發音檢查(建議用電腦版 Chrome / Edge)。');
    note.textContent = '提示:' + msgs.join(' ') + ' 仍可聽示範、看句練習。';
    host.appendChild(note);
  }

  // 課次選擇列
  const lessonRow = document.createElement('div');
  lessonRow.className = 'shadow-lessons';
  lessonRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px;';
  host.appendChild(lessonRow);

  const stage = document.createElement('div');
  stage.className = 'shadow-stage';
  host.appendChild(stage);

  const renderLessonRow = (): void => {
    lessonRow.innerHTML = '';
    lessons.forEach((lesson, i) => {
      const btn = academicButton({
        label: lesson.title,
        variant: i === lessonIndex ? 'primary' : 'secondary',
        onClick: () => {
          if (i === lessonIndex) return;
          stopAll();
          lessonIndex = i;
          sentenceIndex = 0;
          render();
        },
      });
      btn.classList.add('shadow-lesson-tab');
      lessonRow.appendChild(btn);
    });
  };

  const render = (): void => {
    renderLessonRow();
    stage.innerHTML = '';
    const lesson = lessons[lessonIndex];
    if (!lesson) return;
    const sentences = lesson.sentences;
    if (sentenceIndex >= sentences.length) sentenceIndex = sentences.length - 1;
    if (sentenceIndex < 0) sentenceIndex = 0;
    const sentence = sentences[sentenceIndex];
    if (!sentence) return;

    // 進度
    const prog = document.createElement('div');
    prog.className = 'shadow-progress';
    prog.textContent = `第 ${sentenceIndex + 1} / ${sentences.length} 句`;
    prog.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:16px;color:var(--ef-ink);margin-bottom:10px;';
    stage.appendChild(prog);

    // 句卡(英文 + 中文意思)
    const card = document.createElement('div');
    card.className = 'shadow-card';
    card.style.cssText =
      'padding:18px 20px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';
    const en = document.createElement('div');
    en.className = 'shadow-en';
    en.textContent = sentence.en;
    en.style.cssText = 'font-family:var(--ef-serif);font-size:24px;color:var(--ef-ink);line-height:1.5;';
    const zh = document.createElement('div');
    zh.className = 'shadow-zh';
    zh.textContent = sentence.zh;
    zh.style.cssText = 'margin-top:8px;font-size:15px;color:var(--ef-ink-soft);line-height:1.6;';
    card.append(en, zh);
    stage.appendChild(card);

    // ── 操作列 ──
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;margin-top:16px;align-items:center;';
    stage.appendChild(controls);

    const demoBtn = academicButton({
      label: '▶ 聽示範',
      variant: 'secondary',
      onClick: () => playDemo(sentence),
    });
    demoBtn.classList.add('shadow-demo');
    controls.appendChild(demoBtn);

    const result = document.createElement('div');
    result.className = 'shadow-result';
    result.style.cssText = 'margin-top:14px;display:flex;flex-direction:column;gap:10px;';

    let recording = false;
    const recBtn = academicButton({
      label: recordOk ? '🎙️ 跟讀錄音' : '🎙️ 發音檢查',
      variant: 'primary',
      onClick: () => {
        if (recording) { void finishRecording(); return; }
        void beginRecording();
      },
    });
    recBtn.classList.add('shadow-record');
    controls.appendChild(recBtn);

    const timerEl = document.createElement('span');
    timerEl.className = 'shadow-timer';
    timerEl.style.cssText = 'font-size:14px;color:var(--ef-sub);min-width:64px;';
    controls.appendChild(timerEl);

    let liveTranscriptEl: HTMLElement | null = null;
    let recognitionPromise: Promise<string> | null = null;

    const beginRecording = async (): Promise<void> => {
      recording = true;
      recBtn.textContent = '⏹ 停止';
      result.innerHTML = '';
      let secs = 0;
      timerEl.textContent = '0 秒';
      recTimer = setInterval(() => { secs += 1; timerEl.textContent = `${secs} 秒`; }, 1000);

      if (speechOk) {
        liveTranscriptEl = document.createElement('div');
        liveTranscriptEl.style.cssText =
          'font-size:14px;color:var(--ef-ink-soft);min-height:20px;font-style:italic;';
        liveTranscriptEl.textContent = '聽你說…';
        result.appendChild(liveTranscriptEl);
        if (!result.isConnected) stage.appendChild(result);
        const { promise, handle } = recognizeSpeech('en-US', (t) => {
          if (liveTranscriptEl) liveTranscriptEl.textContent = t ? `「${t}」` : '聽你說…';
        });
        recognition = handle;
        recognitionPromise = promise.catch(() => '');
      }

      if (recordOk) {
        try {
          recorder = await startRecording();
        } catch (e) {
          console.warn('[shadowing] 錄音啟動失敗(改用發音檢查):', e);
          recorder = null;
        }
      }
    };

    const finishRecording = async (): Promise<void> => {
      recording = false;
      recBtn.textContent = recordOk ? '🎙️ 重新跟讀' : '🎙️ 再檢查一次';
      if (recTimer) { clearInterval(recTimer); recTimer = null; }
      timerEl.textContent = '';

      try { recognition?.stop(); } catch { /* noop */ }
      recognition = null;
      const transcript = recognitionPromise ? await recognitionPromise : '';
      recognitionPromise = null;

      let audioUrl: string | null = null;
      if (recorder) {
        try {
          const { url } = await recorder.stop();
          audioUrl = url;
          if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } }
          lastUrl = url;
        } catch (e) {
          console.warn('[shadowing] 錄音停止失敗:', e);
        }
        recorder = null;
      }

      renderResult(transcript, audioUrl);
    };

    const renderResult = (transcript: string, audioUrl: string | null): void => {
      result.innerHTML = '';
      if (!result.isConnected) stage.appendChild(result);

      if (audioUrl) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:10px;';
        const label = document.createElement('span');
        label.textContent = '你的錄音:';
        label.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);';
        const audio = document.createElement('audio');
        audio.controls = true;
        audio.src = audioUrl;
        audio.className = 'shadow-playback';
        row.append(label, audio);
        result.appendChild(row);
      }

      if (speechOk) {
        const score = scorePronunciation(sentence.en, transcript);
        const rating = ratePronunciation(score.coverage);

        // P3c 追蹤鉤子:每句最近分數落地 localStorage(只存資料,不做 UI)。
        recordShadowingScore(sentence.id, { percent: rating.percent, missed: score.missed });

        const toneColor =
          rating.tone === 'good' ? 'var(--ef-green)'
            : rating.tone === 'ok' ? 'var(--ef-ink-soft)'
              : 'var(--ef-danger, #b4434a)';
        const badge = document.createElement('div');
        badge.className = 'shadow-score';
        badge.style.cssText =
          `display:inline-flex;align-items:center;gap:8px;font-family:var(--ef-serif);font-weight:700;` +
          `font-size:18px;color:${toneColor};`;
        badge.textContent = `發音 ${rating.percent}% · ${rating.label}`;
        result.appendChild(badge);

        if (transcript.trim().length > 0) {
          const detail = document.createElement('div');
          detail.className = 'shadow-detail';
          detail.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);line-height:1.8;';
          const heard = document.createElement('div');
          heard.textContent = `辨識到:${transcript}`;
          detail.appendChild(heard);
          if (score.missed.length > 0) {
            const miss = document.createElement('div');
            miss.className = 'shadow-missed';
            miss.innerHTML =
              `漏唸 / 待加強:<span style="color:var(--ef-danger,#b4434a);">${score.missed.join('、')}</span>`;
            detail.appendChild(miss);
          }
          result.appendChild(detail);
        } else {
          const noSpeech = document.createElement('div');
          noSpeech.textContent = '沒聽到聲音,再大聲念一次試試。';
          noSpeech.style.cssText = 'font-size:14px;color:var(--ef-sub);';
          result.appendChild(noSpeech);
        }
      }
    };

    stage.appendChild(result);

    // ── 上下句 ──
    const nav = document.createElement('div');
    nav.style.cssText = 'display:flex;gap:10px;margin-top:18px;';
    const prev = academicButton({
      label: '← 上一句',
      variant: 'secondary',
      onClick: () => { if (sentenceIndex > 0) { stopAll(); sentenceIndex -= 1; render(); } },
    });
    prev.classList.add('shadow-prev');
    const next = academicButton({
      label: '下一句 →',
      variant: 'secondary',
      onClick: () => { if (sentenceIndex < sentences.length - 1) { stopAll(); sentenceIndex += 1; render(); } },
    });
    next.classList.add('shadow-next');
    (prev as HTMLButtonElement).disabled = sentenceIndex === 0;
    (next as HTMLButtonElement).disabled = sentenceIndex === sentences.length - 1;
    nav.append(prev, next);
    stage.appendChild(nav);
  };

  render();

  return { destroy: stopAll };
}
